// src/App.tsx

import { lazy, Suspense, useEffect, useRef } from "react";
import {
    Routes,
    Route,
    Navigate,
    useParams,
    useLocation,
} from "react-router-dom";
import { telemetry } from "@bridge-applications/telemetry-js";
import Layout from "./components/app/Layout";
import MainPage from "./components/pages/MainPage/MainPage";
import useWindowWidth from "./logic/hooks/useWindowWidth";
import "./components/app/styles/globals.scss";

// Lazy-loaded pages (not needed on initial render)
const NotSupported = lazy(
    () => import("./components/pages/NotSupported/NotSupported"),
);
const TaskPage = lazy(() => import("./components/pages/TaskPage/TaskPage"));
const SettingsPage = lazy(
    () => import("./components/pages/SettingsPage/SettingsPage"),
);
const MailPage = lazy(
    () => import("./components/pages/browser/MailPage/MailPage"),
);
const SpinTheWheelPage = lazy(
    () => import("./components/pages/SpinTheWheelPage/SpinTheWheelPage"),
);
const MapPage = lazy(() => import("./components/pages/MapPage/MapPage"));
const ReferralInstructions = lazy(
    () =>
        import("./components/pages/ReferralInstructions/ReferralInstructions"),
);
const ReferralPage = lazy(
    () => import("./components/pages/ReferralPage/ReferralPage"),
);
const ReferralPageV2 = lazy(
    () => import("./components/pages/ReferralPageV2/ReferralPageV2"),
);
const BusinessPage = lazy(
    () => import("./components/pages/BusinessPage/BusinessPage"),
);
const PollManagerPage = lazy(
    () =>
        import("./components/pages/BusinessPage/comps/PollManagerPage/PollManagerPage"),
);
const AbPage = lazy(() => import("./components/pages/AbPage/AbPage"));
const TacPage = lazy(() => import("./components/pages/TacPage/TacPage"));
const GaePage = lazy(() => import("./components/pages/GaePage/GaePage"));
const GameCataloguePage = lazy(
    () => import("./components/pages/GameCataloguePage/GameCataloguePage"),
);
const GaeMapPage = lazy(
    () => import("./components/pages/GaePage/pages/GaeMapPage/GaeMapPage"),
);
const GaeBattlePage = lazy(
    () =>
        import("./components/pages/GaePage/pages/GaeBattlePage/GaeBattlePage"),
);
const GaeProfilePage = lazy(
    () =>
        import("./components/pages/GaePage/pages/GaeProfilePage/GaeProfilePage"),
);

function ReferralRoute() {
    const { referralCode } = useParams<{ referralCode: string }>();
    const isValid = !!referralCode && /^[A-Z0-9]{5}$/.test(referralCode);
    return isValid ? <ReferralInstructions /> : <Navigate to="/" replace />;
}

export default function App() {
    const width = useWindowWidth();
    const { pathname } = useLocation();
    const notiTrackedRef = useRef(false);

    // Track notification open
    useEffect(() => {
        if (notiTrackedRef.current) return;
        try {
            const params = new URLSearchParams(window.location.search);
            const notiId = params.get("noti");
            if (notiId) {
                notiTrackedRef.current = true;
                telemetry.capture("noti__open", { noti_id: notiId });
            }
        } catch (e) {
            console.warn("Failed to track notification open", e);
        }
    }, []);

    const widthExemptPaths = ["/terms-and-conditions", "/14"];
    if (width > 600 && !widthExemptPaths.includes(pathname)) {
        return <Navigate to="/not-supported" replace />;
    }

    return (
        <Suspense fallback={null}>
            <Routes>
                {/* unsupported page */}
                <Route path="/not-supported" element={<NotSupported />} />

                <Route path="/terms-and-conditions" element={<TacPage />} />
                <Route
                    path="/14"
                    element={<Navigate to="/terms-and-conditions" replace />}
                />

                {/* all other routes live under Layout */}
                <Route element={<Layout />}>
                    <Route path="/" element={<MainPage />} />
                    <Route path="/business" element={<BusinessPage />} />
                    <Route path="/ab-page" element={<AbPage />} />
                    <Route path="/game" element={<GaePage />}>
                        <Route index element={<Navigate to="map" replace />} />
                        <Route path="map" element={<GaeMapPage />} />
                        <Route path="battle" element={<GaeBattlePage />} />
                        <Route path="profile" element={<GaeProfilePage />} />
                    </Route>
                    <Route
                        path="/game-catalogue"
                        element={<GameCataloguePage />}
                    />
                    <Route
                        path="/business/poll/:id"
                        element={<PollManagerPage />}
                    />
                    <Route path="/spin" element={<SpinTheWheelPage />} />
                    <Route path="/referral" element={<ReferralPage />} />
                    <Route path="/referral-v2" element={<ReferralPageV2 />} />
                    <Route path="/map" element={<MapPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                </Route>

                <Route path="/:referralCode" element={<ReferralRoute />} />

                {/* standalone routes */}
                <Route path="/browser/mail" element={<MailPage />} />
                <Route path="/task/:id" element={<TaskPage />} />

                {/* catch‑all: if someone types an invalid URL while narrow, send them home */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    );
}
